import { useMemo } from 'react';
import { keyById } from '@mews/fp';
import { AvailabilityBlock } from '@mews-commander/data-access';
import { DateTimeUtils, useDateTimeLocalizationInfo, useLocalizeText, useTimezone } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { splitAvailabilitiesByOverbooking, useServiceAvailabilityQueries } from '../../../../availability';

export const useOverbookingMessage = (
    serviceId: string,
    startUtc: string | undefined,
    endUtc: string | undefined,
    resourceCategoryId: string | undefined
) => {
    const lt = useLocalizeText();
    const timezone = useTimezone();
    const localizationInfo = useDateTimeLocalizationInfo();

    const availabilitiesResult = useServiceAvailabilityQueries(
        serviceId,
        startUtc && endUtc ? [{ startUtc, endUtc }] : []
    );

    const overbookingMessage = useMemo(() => {
        const splitAvailabilities = resourceCategoryId
            ? splitAvailabilitiesByOverbooking(
                  availabilitiesResult.flatMap((result) => result.data?.availabilities ?? []),
                  resourceCategoryId,
                  1
              )
            : null;

        const propertyOverbookingAvailabilities = splitAvailabilities?.propertyOverbookingAvailabilities;

        if (!propertyOverbookingAvailabilities?.length) {
            return null;
        }

        const availabilityWithBlockId = propertyOverbookingAvailabilities?.find(
            ({ blockAdjustmentData }) => blockAdjustmentData?.find((data) => data.availabilityBlockId)
        );

        const availabilityBlockId = availabilityWithBlockId?.blockAdjustmentData?.[0].availabilityBlockId;

        if (availabilityBlockId) {
            const availabilityBlocks = keyById(
                availabilitiesResult.flatMap((result) => result.data?.availabilityBlocks ?? [])
            ) as Record<string, AvailabilityBlock>;

            const ab = availabilityBlocks[availabilityBlockId];

            return lt({
                textKey: TextKey.NewReservationPanelOverbookingError,
                substitutes: {
                    from: DateTimeUtils.dateTimeUtcStringToDateString(ab.startUtc, localizationInfo, timezone),
                    to: DateTimeUtils.dateTimeUtcStringToDateString(ab.endUtc, localizationInfo, timezone),
                },
            });
        }

        return lt({
            textKey: TextKey.NewReservationPanelOverbookingError,
            substitutes: {
                from: DateTimeUtils.dateTimeUtcStringToDateString(
                    propertyOverbookingAvailabilities[0].timeUnitBaseStartUtc,
                    localizationInfo,
                    timezone
                ),
                to: DateTimeUtils.dateTimeUtcStringToDateString(
                    propertyOverbookingAvailabilities[propertyOverbookingAvailabilities.length - 1]
                        .timeUnitBaseStartUtc,
                    localizationInfo,
                    timezone
                ),
            },
        });
    }, [resourceCategoryId, availabilitiesResult, lt, localizationInfo, timezone]);

    return useMemo(
        () => ({
            overbookingMessage,
            loading: availabilitiesResult.some((result) => result.isFetching),
        }),
        [availabilitiesResult, overbookingMessage]
    );
};
